import { Toast } from "primereact/toast";
import Decimal from 'decimal.js';
import { useEffect, useRef } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import { getTimezone as resolveTimezone } from "../utils/timezone";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

/**
 * phoneNumberFormatter - function to format phone number
 * @function
 */
const phoneNumberFormatter = (phone: string): string => {
    // Remove non-numeric characters
    phone = phone.replace(/\D/g, "");
    // apply mask
    phone = phone.replace(
        /^(\d{2})(\d{1})(\d{2})(\d{2})(\d{2})(\d{2})$/,
        "($1) $2 $3$4-$5$6"
    );
    return phone;
};

/**
 * currencyFormatter - function to format local currency
 * @function
 */
const currencyFormatter = (value: string): string => {
    // Remove non-numeric characters
    const numericValue = value.replace(/[^\d]/g, "");

    if (numericValue !== "") {
        // formar to br currency
        const formattedValue = new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
        }).format(parseFloat(numericValue) / 100);

        // if the values is a negative value it will be keeped
        if (value.includes("-")) {
            return formattedValue.replace("-", "- ");
        }
        return formattedValue;
    }
    return "";
};

/**
 * getRandomColor - function to get a random color and returns a rgb
 * @function
 */
const getRandomColor = () => {
    const red = Math.floor(Math.random() * 101) + 100;
    const green = Math.floor(Math.random() * 101) + 100;
    const blue = Math.floor(Math.random() * 101) + 100;
    return `rgb(${red},${green},${blue})`;
};

/**
 * cpfFormatter - function to format CPF value
 * @function
 */
const cpfFormatter = (cpf: string): string => {
    // Remove non-numeric characters
    cpf = cpf.replace(/\D/g, "");
    // apply mask
    cpf = cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
    return cpf;
};

/**
 * registerFormatter - function to format CPF value
 * @function
 */
const registerFormatter = (retister: string): string => {
    // Remove non-numeric characters
    retister = retister.replace(/\D/g, "");
    return retister;
};

/**
 * CNPJFormatter - function to format CNPJ value
 * @function
 */
const CNPJFormatter = (cnpj: string | null): string | null => {
    // Remove non-numeric characters
    cnpj = cnpj?.replace(/\D/g, "") ?? "";
    // apply mask
    cnpj = cnpj?.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        "$1.$2.$3/$4-$5"
    );
    return cnpj;
};

/**
 * formatterZipCode - function to format zip code value
 * @function
 */
const formatterZipCode = (zip: string) => {
    // Remove non-numeric characters
    zip = zip.replace(/\D/g, "");
    // apply mask
    zip = zip.replace(/^(\d{5})(\d{3})$/, "$1-$2");
    return zip;
};

/**
 * setToastRefToOpen - function to render a custom toast
 * @function
 */
const setToastRefToOpen = (
    ref: React.RefObject<Toast>,
    text: String,
    title: string,
    statusMessage?: "warn" | "success" | "info" | "error" | undefined,
    options?: any,
    timeOut?: number,
    sticky?: boolean
) => {
    ref.current?.show({
        sticky: sticky,
        severity: statusMessage,
        summary: title,
        detail: text,
        life: timeOut,
        closable: false,
        ...options,
    });
};

/**
 * getFilteredList - function with a generic type to filter array values
 * @function
 */
const getFilteredList = <T>(
    props: Array<string>,
    list: Array<T>,
    query: string
) => {
    const data = list.filter((item: any) => {
        return props.find((prop: string) => {
            if (
                item?.[prop]
                    ?.toString()
                    .toLowerCase()
                    .includes(query.toString().toLowerCase())
            ) {
                return item;
            }
        });
    });
    return data;
};

/**
 * getFilteredListWithDatabase - function with a generic type to filter array values
 * @function
 */
const getFilteredListWithDatabase = <T>(
    props: Array<string>,
    list: Array<T>,
    query: string,
    fun: (q: string) => void
) => {
    const data = list.filter((item: any) => {
        return props.find((prop: string) => {
            if (
                item?.[prop]
                    ?.toString()
                    .toLowerCase()
                    .includes(query.toString().toLowerCase())
            ) {
                return item;
            }
        });
    });

    if (data.length === 0) {
        fun(query);
        return [];
    }
    return data;
};

/**
 * getFilteredMultipleList - function with a generic type to filter array values
 * @function
 */
const getFilteredMultipleList = <T>(
    props: Array<string>,
    list: Array<T>,
    queries: Array<string>
) => {
    const data = list.filter((item: any) => {
        return queries.every((query: string) => {
            return props.some((prop: string) => {
                if (
                    item?.[prop]
                        ?.toString()
                        .toLowerCase()
                        .includes(query.toString().toLowerCase())
                ) {
                    return item;
                }
            });
        });
    });
    return data;
};

/**
 * usePrevious - function with a generic type to use previous state value
 * @function
 */
const usePrevious = <T>(value: T) => {
    const ref = useRef<T | null>(null);

    useEffect(() => {
        ref.current = value;
    });

    return ref.current;
};

/**
 * formatString - function to format string
 * @function
 */
const formatString = (str: string) => {
    return str
        .normalize("NFD") // Decompõe caracteres acentuados
        .replace(/[\u0300-\u036f]/g, "") // Remove marcas de diacríticos
        .toLocaleLowerCase() // Converte para minúsculas
        .replace(/ /g, "-"); // Substitui espaços por hífens
};

function processInput(inputValue: any): number | null {
    if (inputValue == null || Number.isNaN(inputValue)) return null;
    return Number(inputValue);
}

const DEFAULT_LOCALE = { code: "pt-BR", currency: "BRL" };

const fNumber = (inputValue: number, options?: any) => {
    const number = processInput(inputValue);
    if (number === null) return "";

    const fm = new Intl.NumberFormat(DEFAULT_LOCALE.code, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        ...options,
    }).format(number);
    return fm;
};

const flespiEventHandlerMessage = (message: string) => {
    let event = "";
    switch (message) {
        case "Answer and call the police":
            event = "Uso de telefone";
            break;
        case "Fatique driving alarm":
            event = "Fadiga";
            break;
        case "Distracted driving alarm":
            event = "Desvio de atenção";
            break;
        case "Smoking alarm":
            event = "Fumando na condução";
            break;
        default:
            event = "Evento de condução";
            break;
       }
    return event;
};

const eventHandlerMessage = (message: string) => {
    let event = "";
    switch (message) {
        case "deviceOnline":
            event = "Dipositivo online.";
            break;
        case "deviceOffline":
            event = "Dipositivo offline.";
            break;
        case "deviceInactive":
            event = "Dipositivo inativo.";
            break;
        case "queuedCommandSent":
            event = "Comando enviado.";
            break;
        case "deviceMoving":
            event = "Dispositivo em movimento.";
            break;
        case "deviceStopped":
            event = "Dispositivo parado.";
            break;
        case "deviceOverspeed":
            event = "Dispositivo acima da velocidade.";
            break;
        case "deviceFuelDrop":
            event = "Queda de combustível detectada.";
            break;
        case "geofenceEnter":
            event = "Dispositivo entrou na cerca.";
            break;
        case "geofenceExit":
            event = "Dispositivo saiu da cerca.";
            break;
        case "ignitionOn":
            event = "Ignição ligada.";
            break;
        case "ignitionOff":
            event = "Ignição desligada.";
            break;
        case "driverChanged":
            event = "Mudança de motorista.";
            break;
        case "lowspeed":
            event = "Baixa velocidade.";
            break;
        case "overSpeed":
        case "overspeed":
            event = "Veículo acima da velocidade permitida.";
            break;
        case "lowBattery":
            event = "Bateria baixa.";
            break;
        case "powerCut":
            event = "Alimentação do dispositivo interrompida.";
            break;
        case "powerOff":
            event = "Dispositivo sem alimentação de energia.";
            break;
        case "hardAcceleration":
            event = "Aceleração brusca.";
            break;
        case "hardBraking":
            event = "Freada brusca.";
            break;
        case "hardCornering":
            event = "Curva fechada.";
            break;
        case "fuelLeak":
            event = "Possível vazamento de combustível.";
            break;
        case "highRpm":
            event = "RPM alto.";
            break;
        case "sos":
            event = "Precisa de ajuda, alerta SOS.";
            break;
        default:
            event = "Evento de rastreamento.";
            break;
    }
    return event;
};

Decimal.set({ precision: 20, rounding: Decimal.ROUND_HALF_EVEN });
const convertMetersToKilometersOdometer = (meters: number | string): Decimal => {
    const divisor = new Decimal(1000);
    const result = new Decimal(meters).div(divisor);
    return result?.toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN);
};

const formattedSpeed = (speed?: number | string | null): string => {
    if (speed == null || Number.isNaN(Number(speed))) return "0 Km/h";
    try {
        const speedDecimal = new Decimal(speed);
        return `${speedDecimal?.toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN).toString()} Km/h`;
    } catch {
        return "0 Km/h";
    }
};

const formatVoltage = (value: any, suffix: string) => isFinite(Number(value)) ? `${Number(value).toFixed(3)} ${suffix}` : "-";

const getTimezone = resolveTimezone;

const getUtcOffsetByTimezone = (
    timezone: string,
    date?: string
): number => {
    const resolvedTimezone = getTimezone(timezone);
    const referenceDate = date ? dayjs(date) : dayjs();

    return referenceDate
        .tz(resolvedTimezone)
        .utcOffset() / 60;
};

/*const getUtcOffsetByTimezone = (timezone: string): number => {
    switch (timezone) {
        case "America/Noronha":
            return -2;
        case "America/Sao_Paulo":
            return -3;
        case "America/Cuiaba":
        case "America/Manaus":
            return -4;
        case "America/Rio_Branco":
            return -5;
        default:
            return -3; // Padrão: Brasília
    }
};*/

const getDistanceKm = (
    lat1: number, lng1: number,
    lat2: number, lng2: number
): number => {
    const R = 6371; // raio da terra em km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) ** 2;
    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

const findNearestVehicle = <T extends { id: any; position?: { lat: number; lng: number } }>(
    origin: T,
    list: T[]
): T | null => {
    let nearest: T | null = null;
    let minDist = Infinity;

    list.forEach((item) => {
        if (item.id === origin.id || item.position?.lat == null || item.position?.lng == null) return;
        const dist = getDistanceKm(
            origin.position!.lat, origin.position!.lng,
            item.position.lat, item.position.lng
        );
        if (dist < minDist) {
            minDist = dist;
            nearest = item;
        }
    });

    return nearest;
};

const vehicleIgnitionStatusColors = {
    moving: "#00B700",
    idle: "#ed6c02",
    off: "rgb(255, 69, 96)",
};

// idle vem da API: ignição ligada e parado há pelo menos o tempo ocioso do cliente (padrão 5 min).
// Antes disso o veículo parado com ignição ligada segue verde; sem a informação, mantém a regra antiga.
const getVehicleIgnitionStatus = (
    attributes?: { ignition?: boolean | null; motion?: boolean | null } | null,
    protocol?: string | null,
    idle?: boolean | null
) => {
    const ignition = attributes?.ignition;
    const motion = attributes?.motion;
    const stoppedWithIgnition = Boolean(ignition && !motion);
    const isIdle = stoppedWithIgnition && idle !== false;
    // Preserva a indicação por movimento dos rastreadores Globalstar.
    const status = (ignition && motion) || (protocol === "globalstar" && motion) || (stoppedWithIgnition && !isIdle)
        ? "moving"
        : isIdle ? "idle" : "off";
    const ignitionLabel = ignition == null ? "Ignição não informada" : ignition ? "Ignição ligada" : "Ignição desligada";
    const motionLabel = motion == null
        ? "movimento não informado"
        : motion ? "em movimento" : isIdle ? "tempo ocioso" : "veículo parado";

    return {
        status,
        color: vehicleIgnitionStatusColors[status],
        label: `${ignitionLabel} · ${motionLabel}`,
    };
};

const getApiErrorMessage = (error: any, fallback: string): string => {
    let data = error?.response?.data;

    try {
        if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
            data = new TextDecoder().decode(data);
        }
        if (typeof data === "string") {
            data = JSON.parse(data);
        }
    } catch {
        // Respostas HTML do servidor/proxy ou JSON inválido não devem quebrar o toast.
        return fallback;
    }

    const reason = data?.reason ?? data?.detail ?? data?.message;

    if (Array.isArray(reason)) {
        return reason.filter(Boolean).join(". ") || fallback;
    }

    return typeof reason === "string" && reason.trim() ? reason : fallback;
};

const isMqttCommandProtocol = (protocol?: string | null): boolean =>
    protocol?.trim().toLowerCase() === "teltonikamqtt";

type MqttCommandStatusSeverity = "warn" | "success" | "info" | "error";

const mqttCommandStatusPalette: Record<MqttCommandStatusSeverity, { main: string; background: string }> = {
    info: { main: "#0288d1", background: "rgba(2, 136, 209, 0.08)" },
    success: { main: "#2e7d32", background: "rgba(46, 125, 50, 0.08)" },
    warn: { main: "#ed6c02", background: "rgba(237, 108, 2, 0.08)" },
    error: { main: "#d32f2f", background: "rgba(211, 47, 47, 0.08)" },
};

const getMqttCommandStatusCardStyle = (severity: MqttCommandStatusSeverity) => {
    const colors = mqttCommandStatusPalette[severity];

    return {
        border: `1px solid ${colors.main}`,
        borderLeft: `5px solid ${colors.main}`,
        backgroundColor: colors.background,
        borderRadius: 2,
        fontFamily: "Prometo",
    };
};

const getMqttCommandStatusInfo = (status?: string | null): {
    label: string;
    severity: MqttCommandStatusSeverity;
    terminal: boolean;
} => {
    switch (status?.trim().toUpperCase()) {
        case "PENDING_PUBLISH":
            return { label: "Aguardando publicação", severity: "info", terminal: false };
        case "PUBLISHING":
            return { label: "Publicando comando", severity: "info", terminal: false };
        case "PUBLISHED":
            return { label: "Comando publicado", severity: "info", terminal: false };
        case "RECEIVED_BY_GATEWAY":
            return { label: "Recebido pelo gateway", severity: "info", terminal: false };
        case "SENT_TO_DEVICE":
            return { label: "Enviado ao rastreador", severity: "info", terminal: false };
        case "RESPONDED":
            return { label: "Comando respondido", severity: "success", terminal: true };
        case "DEVICE_NACK":
            return { label: "Comando recusado pelo rastreador", severity: "error", terminal: true };
        case "REJECTED":
            return { label: "Comando rejeitado", severity: "error", terminal: true };
        case "EXPIRED":
            return { label: "Prazo do comando expirado", severity: "warn", terminal: true };
        case "BUSY":
            return { label: "Rastreador ocupado", severity: "warn", terminal: true };
        case "OFFLINE":
            return { label: "Rastreador offline", severity: "warn", terminal: true };
        case "FAILED":
            return { label: "Falha no envio do comando", severity: "error", terminal: true };
        case "TIMEOUT":
            return { label: "Rastreador não respondeu no prazo", severity: "warn", terminal: true };
        case "DELIVERY_UNKNOWN":
            return { label: "Não foi possível confirmar a entrega", severity: "warn", terminal: true };
        case "CANCELLED":
            return { label: "Comando cancelado", severity: "info", terminal: true };
        default:
            return {
                label: status?.trim().replace(/_/g, " ") || "Status não informado",
                severity: "info",
                terminal: false,
            };
    }
};

const findNearestPoints = <T extends { id: any; latitude: number; longitude: number }>(
    origin: { lat: number; lng: number },
    list: T[],
    limit: number = 5
): Array<T & { distanceKm: number }> => {
    return list
        .filter((item) => item.latitude != null && item.longitude != null)
        .map((item) => ({
            ...item,
            distanceKm: getDistanceKm(origin.lat, origin.lng, item.latitude, item.longitude),
        }))
        .sort((a, b) => a.distanceKm - b.distanceKm)
        .slice(0, limit);
};

const buildGoogleMapsLink = (lat: number, lng: number): string => {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
};

const buildInterestPointShareMessage = (item: {
    name: string;
    description?: string | null;
    address?: string | null;
    latitude: number;
    longitude: number;
}): string => {
    const mapsUrl = buildGoogleMapsLink(item.latitude, item.longitude);
    return (
        `*PONTO DE INTERESSE*\n\n` +
        `*Nome:* ${item.name}\n` +
        (item.description ? `*Descrição:* ${item.description}\n` : '') +
        (item.address ? `*Endereço:* ${item.address}\n` : '') +
        `\n*Localização no mapa:*\n${mapsUrl}`
    );
};

const buildAllInterestPointsShareMessage = (
    list: Array<{ name: string; latitude: number; longitude: number }>
): string => {
    const header = `*PONTOS DE INTERESSE ATIVOS* (${list.length})\n\n`;
    const items = list
        .map((item, index) => `${index + 1}. *${item.name}*\n${buildGoogleMapsLink(item.latitude, item.longitude)}`)
        .join('\n\n');
    return header + items;
};

const openWhatsAppShare = (message: string): void => {
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
};

export {
    phoneNumberFormatter,
    currencyFormatter,
    buildInterestPointShareMessage,
    buildAllInterestPointsShareMessage,
    openWhatsAppShare,
    buildGoogleMapsLink,
    getRandomColor,
    findNearestPoints,
    cpfFormatter,
    registerFormatter,
    setToastRefToOpen,
    getFilteredList,
    formatterZipCode,
    usePrevious,
    CNPJFormatter,
    getFilteredMultipleList,
    formatString,
    fNumber,
    eventHandlerMessage,
    convertMetersToKilometersOdometer,
    formattedSpeed,
    getFilteredListWithDatabase,
    formatVoltage,
    flespiEventHandlerMessage,
    getTimezone,
    getUtcOffsetByTimezone,
    getDistanceKm,
    findNearestVehicle,
    vehicleIgnitionStatusColors,
    getVehicleIgnitionStatus,
    getApiErrorMessage,
    isMqttCommandProtocol,
    getMqttCommandStatusInfo,
    mqttCommandStatusPalette,
    getMqttCommandStatusCardStyle
};
